import { useState } from "react";
import { useLazyQuery, useMutation, gql } from "@apollo/client";
import { notify, notifyError } from "../../../utils/toast";
import { AdminSearchListDetailLayout } from "../components/admin-search-list-detail-layout";

/** 유저 삭제·쿠폰 발행 어드민과 동일한 searchUsers 쿼리 사용 */
const SEARCH_USERS = gql`
  query SearchUsersForNameChange($keyword: String) {
    searchUsers(keyword: $keyword) {
      id
      name
      email
      phone
    }
  }
`;

const ADMIN_UPDATE_USER_NAME = gql`
  mutation AdminUpdateUserName($userId: Int!, $name: String!) {
    adminUpdateUserName(userId: $userId, name: $name) {
      ok
      error
    }
  }
`;

type UserRow = {
  id: number;
  name: string | null;
  email: string;
  phone: string | null;
};

export default function AdminUserNameChange() {
  const [searchKeyword, setSearchKeyword] = useState("");
  const [selectedUser, setSelectedUser] = useState<UserRow | null>(null);
  const [nameValue, setNameValue] = useState("");

  const [searchUsers, { data: searchData, loading: searchLoading }] =
    useLazyQuery(SEARCH_USERS, { fetchPolicy: "network-only" });

  const [adminUpdateUserName, { loading: updating }] = useMutation(
    ADMIN_UPDATE_USER_NAME,
    {
      onCompleted: (res) => {
        if (res.adminUpdateUserName.ok) {
          notify("이름이 변경되었습니다.");
          setSelectedUser((prev) =>
            prev ? { ...prev, name: nameValue.trim() } : null
          );
          setNameValue("");
        } else {
          notifyError(
            res.adminUpdateUserName.error ?? "이름 변경에 실패했습니다."
          );
        }
      },
      onError: (e) => {
        notifyError(e.message);
      },
    }
  );

  const users: UserRow[] = searchData?.searchUsers ?? [];

  const handleSearch = () => {
    const term = searchKeyword.trim();
    if (!term) {
      notifyError("검색어를 입력해주세요.");
      return;
    }
    searchUsers({ variables: { keyword: term } });
  };

  const handleSelectUser = (user: UserRow) => {
    setSelectedUser(user);
    setNameValue(user.name ?? "");
  };

  const handleSubmitName = () => {
    if (!selectedUser) {
      notifyError("유저를 선택해주세요.");
      return;
    }
    const trimmed = nameValue.trim();
    if (!trimmed) {
      notifyError("이름을 입력해주세요.");
      return;
    }
    adminUpdateUserName({
      variables: { userId: selectedUser.id, name: trimmed },
    });
  };

  const listContent =
    users.length === 0 ? (
      <div className="p-6 text-center text-gray-500 text-sm">
        검색어를 입력하고 검색 버튼을 누르세요.
      </div>
    ) : (
      <ul className="divide-y divide-gray-200">
        {users.map((user) => {
          const isSelected = selectedUser?.id === user.id;
          return (
            <li
              key={user.id}
              onClick={() => handleSelectUser(user)}
              className={`px-4 py-3 cursor-pointer transition-colors ${
                isSelected
                  ? "bg-blue-50 border-l-4 border-l-blue-600"
                  : "hover:bg-gray-50"
              }`}
            >
              <div className="font-medium text-gray-900">
                {user.name || "—"} (ID: {user.id})
              </div>
              <div className="text-xs text-gray-500 truncate">
                {user.email}
                {user.phone ? ` · ${user.phone}` : ""}
              </div>
            </li>
          );
        })}
      </ul>
    );

  return (
    <AdminSearchListDetailLayout
      title="유저 이름 변경"
      description="1) 검색 후 2) 유저를 선택하고 3) 이름을 입력해 변경하세요."
      searchPlaceholder="이름, 이메일, 연락처로 검색"
      searchValue={searchKeyword}
      onSearchChange={setSearchKeyword}
      onSearch={handleSearch}
      searchLoading={searchLoading}
      step1Title="1. 유저 검색"
      step2Title="2. 검색 결과에서 선택"
      step3Title="3. 이름 변경"
      listContent={listContent}
      emptyListText="검색어를 입력하고 검색 버튼을 누르세요."
      rightPanelContent={
        selectedUser ? (
          <>
            <div className="text-sm text-gray-700">
              <span className="font-medium">선택 유저:</span> ID{" "}
              {selectedUser.id}
              {selectedUser.email && <> · {selectedUser.email}</>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                변경할 이름
              </label>
              <input
                type="text"
                value={nameValue}
                onChange={(e) => setNameValue(e.target.value)}
                placeholder="이름 입력"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <button
              type="button"
              disabled={!nameValue.trim() || updating}
              onClick={handleSubmitName}
              className="w-full px-4 py-3 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {updating ? "저장 중..." : "이름 변경"}
            </button>
          </>
        ) : null
      }
      rightPanelEmptyText="왼쪽에서 유저를 검색한 뒤 한 명을 선택하세요."
    />
  );
}
